import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  makeStyles,
} from "@material-ui/core"
import Input from "../../atoms/input/Input"
import { colors } from "../../../utils/theme"

interface TranscriptEditSessionDialogProps {
  isOpen: boolean
  title: string
  /** Value for `<input type="datetime-local">` (local wall clock). */
  startedAtLocal: string
  errorMessage: string | null
  isSaving: boolean
  onClose: () => void
  onTitleChange: (title: string) => void
  onStartedAtLocalChange: (value: string) => void
  onSubmit: () => void | Promise<void>
}

/**
 * Edit transcript session title and clinical date/time.
 */
export default function TranscriptEditSessionDialog({
  isOpen,
  title,
  startedAtLocal,
  errorMessage,
  isSaving,
  onClose,
  onTitleChange,
  onStartedAtLocalChange,
  onSubmit,
}: TranscriptEditSessionDialogProps) {
  const classes = useStyles()

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Edit session details</DialogTitle>
      <DialogContent className={classes.content}>
        <Input
          autoFocus
          fullWidth
          label="Session title"
          value={title}
          onChange={(event) => onTitleChange(event.target.value)}
          error={Boolean(errorMessage)}
          helperText={errorMessage}
          inputProps={{ maxLength: 200 }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              void onSubmit()
            }
          }}
        />
        <Input
          fullWidth
          label="Session date & time"
          type="datetime-local"
          value={startedAtLocal}
          onChange={(event) => onStartedAtLocalChange(event.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="When this session took place (your local time)."
        />
        <Typography variant="body2" className={classes.hint}>
          Changing the date updates how this session appears in Sessions and the
          calendar.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={() => void onSubmit()}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const useStyles = makeStyles((theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
  hint: {
    color: colors.neutral.asphalt,
    lineHeight: 1.45,
  },
}))
